// THE MAIN AREA OVER A PAGE (AI_ASSISTANT_SPEC v1.1 §12.2; Nika 2026-09-29
// "make main thread ai as default" — main thread = "Everywhere").
//
// With the main placement, a send on ANY route opens the answer in the main
// area. On the home that area is the widget grid (pages/main.js mounts the
// views there). On every other route it is THIS layer: one sheet over the
// page's own sheet — the same band (`--slot-top` to `--slot-bottom`, the X2
// gutters, clear of the module rail), the same ground and the same float the
// PageSlot pane wears — holding the SAME views: the main thread, a
// generated board, a live deck, the fly canvas. ONE view at a time, like on
// the home.
//
// The page stays MOUNTED underneath and keeps its state (its scroll, its open
// panes, its drafts); while the layer is up the shell's stylesheet hides the
// page sheet from the eye, the pointer, the Tab order and assistive
// technology (app.js, the `AssistantMainLayer` rule), and the shell's overlay
// reader names the layer as the top layer (`shellOverlayOpen` → 'mainThread'),
// so a page's own Escape stands down: ONE Escape closes the thread and the
// page is back — a second one reaches the page. A page SHEET opened in the
// PageSlot sits above the layer (z 1300 over 1250); the composer stays above
// both (1400).
//
// The layer holds no logic: its gate is functions/assistantMainThread.js
// `assistantMainLayerShown`; each view gates, crossfades and closes itself.
// Rule 66: closed keeps the box — only opacity and visibility change.
export const AssistantMainLayer = {
  dataMainLayer: true,
  dataOpen: (el, s) => (el.call('assistantMainLayerShown', s.root) ? 'true' : 'false'),
  ariaHidden: (el, s) => (el.call('assistantMainLayerShown', s.root) ? null : 'true'),

  // The page sheet's band: the live-measured gaps to the header and to the
  // composer, the X2 gutters, beside the module rail when one renders. The
  // bottom edge also clears the phone suggestion chip row (`--chip-row`, 0
  // off phones — the shell's other bottom-docked islands do the same), so the
  // band's foot is never under the chips or the composer at any composer
  // height: `--slot-bottom` is the composer's live clearance + the nav gap.
  position: 'fixed',
  zIndex: '1250',
  top: '--slot-top',
  bottom: 'calc(var(--slot-bottom) + var(--chip-row))',
  // `--module-rail-clear` is registered with a 0 initial value (app.js).
  left: 'calc(var(--module-rail-clear) + var(--spacing-X2))',
  right: 'X2',

  // The page sheet's ground and elevation: the content-surface pair (the
  // frame pair AppMain paints — gray2 dark, gray16 light), the brand
  // `pane-ground` theme for the inks, the one float shadow, the sheet radius.
  // `--stage-bg` dissolves inside, like in the PageSlot pane: a view's own
  // `var(--stage-bg, …)` paint is the layer's ground, never a second sheet.
  background: 'gray2',
  '@light': { background: 'gray16' },
  style: { '--stage-bg': 'transparent' },
  theme: 'pane-ground',
  borderWidth: '0',
  shadow: 'float',
  round: 'radiusSheet',

  display: 'flex',
  flexDirection: 'column',
  padding: 'C B',
  overflow: 'auto',
  overscrollBehavior: 'contain',
  boxSizing: 'border-box',

  // The views' crossfade: `C`, opacity only; closed keeps the box and hides
  // after the fade, open shows at once and fades in.
  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity C defaultBezier, visibility 0s linear C',
  '&[data-open="true"]': {
    opacity: '1',
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  // Phones: the page sheet's own posture — full-bleed sides, only the top
  // corners round; the composer below stays reachable.
  '@mobileL': {
    left: '0',
    right: '0',
    padding: 'B A',
    round: 'radiusSheet radiusSheet 0 0'
  },

  subscribeTo: [
    'currentPath',
    'assistantDock',
    '_homeThreadOpen',
    '_mainThreadPath',
    '_aiBoard',
    '_aiDeck',
    '_aiFly',
    // the call's theater (meet functions/meetFly.js)
    'meetFly',
    'meetView',
    'livekitConnected'
  ],

  // The same views the home mounts in its grid area — each gates,
  // crossfades and closes itself. ONE view holds the layer at a time, so a
  // closed view takes no height here (the open one is the scroll height; on
  // the home the grid below keeps its own box instead).
  childProps: {
    flexShrink: '0',
    '&:not([data-open="true"])': { height: '0', minHeight: '0', overflow: 'hidden' }
  },
  // `mainHost: 'over'` — each view's keyboard rows keep their own ids here
  // (functions/assistantMainThread.js assistantMainThreadShortcuts). The
  // views exist here only OFF the home (`if:` — none of them declares its own,
  // so this gate replaces nothing): on the home the grid area's views are the
  // ones on screen, and a second copy would mount the same widget bodies and
  // rows twice behind a closed layer.
  // The thread's own composer inset is the HOME's (it scrolls with the page
  // column there); here the band already ends above the composer and the
  // layer's padding is the gap, so a second inset would only push the last
  // message away from the composer.
  AssistantMainThread: {
    mainHost: 'over',
    paddingBottom: '0',
    if: (el, s) => el.call('assistantMainHost', s.root) === 'over'
  },
  // The board's own stack inset is the home's too (it lies over the grid, out
  // of the flow); here the band ends above the stack, as for the thread.
  AssistantBoardView: {
    mainHost: 'over',
    paddingBottom: '0',
    if: (el, s) => el.call('assistantMainHost', s.root) === 'over'
  },
  AssistantDeckView: {
    mainHost: 'over',
    if: (el, s) => el.call('assistantMainHost', s.root) === 'over'
  },
  // FLY MODE's canvas (functions/assistantFly.js) over the page.
  AssistantFlyView: {
    mainHost: 'over',
    if: (el, s) => el.call('assistantMainHost', s.root) === 'over'
  },
  // A CALL's theater (components/AssistantFlyCallView.js): the call's Fly
  // pages over the call itself, which minimizes into the dock.
  AssistantFlyCallView: {
    mainHost: 'over',
    if: (el, s) => el.call('assistantMainHost', s.root) === 'over'
  }
}
